import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as marketingService from '../../services/marketingService';

const {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiCopy,
  FiList,
  FiX,
  FiSave,
  FiInfo,
  FiEye,
  FiUsers,
  FiCalendar,
  FiGlobe,
  FiMapPin,
} = FiIcons;

const TRIGGER_EVENTS = [
  { value: 'payment_succeeded', label: 'Payment succeeded' },
  { value: 'lead_captured', label: 'Lead captured' },
  { value: 'registration_completed', label: 'Registration completed' },
  { value: 'orientation_registered', label: 'Orientation registered' },
  { value: 'event_reminder', label: 'Event session reminder (registered)' },
  { value: 'event_reminder_unpaid', label: 'Event session reminder (unpaid leads)' },
  { value: 'post_event', label: 'Post event' },
  { value: 'account_created', label: 'Account created' },
  { value: 'sharing_link_created', label: 'Invitation link created' },
  {
    value: 'form_submitted',
    label: 'Form Submitted',
    description: 'Fires when a respondent submits a form, survey, or quiz',
  },
];

const REMINDER_TRIGGERS = ['event_reminder', 'event_reminder_unpaid'];

const REMINDER_INTERVAL_OPTIONS = [
  { value: '3_days', label: '3 days before' },
  { value: '2_days', label: '2 days before' },
  { value: '1_day', label: '1 day before' },
  { value: '1_hour', label: '1 hour before' },
  { value: 'at_time', label: 'At session time' },
];

const EVENT_LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Spanish (es)' },
  { value: 'en', label: 'English (en)' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'webinar', label: 'Webinar' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'conference', label: 'Conference' },
  { value: 'course', label: 'Course' },
];

const EVENT_LOCATION_TYPE_OPTIONS = [
  { value: 'online', label: 'Online (Zoom)' },
  { value: 'in_person', label: 'In-person' },
  { value: 'hybrid', label: 'Hybrid' },
];

const FORM_TYPE_OPTIONS = [
  { value: 'form', label: 'Form' },
  { value: 'survey', label: 'Survey' },
  { value: 'quiz', label: 'Quiz' },
];

const PASSED_OPTIONS = [
  { value: 'true', label: 'Passed (true)' },
  { value: 'false', label: 'Failed (false)' },
];

/**
 * All preset condition keys that the workflow engine evaluates against the
 * trigger context (`build_workflow_context` plus per-trigger additions).
 * Each entry drives the "Add condition..." dropdown, the value editor for the
 * selected key, and the tooltip rendered next to the label.
 */
const ALL_PRESET_CONDITIONS = [
  {
    key: 'event_language',
    label: 'Event language',
    valueType: 'select',
    valueOptions: EVENT_LANGUAGE_OPTIONS,
    tooltip:
      'Filters by the event\'s language column. Use "es" for Spanish or "en" for English. Falls back to "es" when the event has no language set.',
    appliesTo: 'event',
  },
  {
    key: 'event_id',
    label: 'Event ID',
    valueType: 'text',
    placeholder: 'UUID of the event',
    tooltip:
      'UUID of a specific event. Use this to target a single event — most stable choice because it survives renames.',
    appliesTo: 'event',
  },
  {
    key: 'event_title',
    label: 'Event title',
    valueType: 'text',
    placeholder: 'Exact event title',
    tooltip:
      'Exact event title string (case-sensitive). Prefer event_id for stability across event renames.',
    appliesTo: 'event',
  },
  {
    key: 'event_title_includes',
    label: 'Event Title — includes keyword(s)',
    valueType: 'text',
    placeholder: 'webinar, free',
    summaryLabel: 'Title includes',
    helperText:
      'Workflow runs only if the event title contains all of these keywords (comma-separated, case-insensitive)',
    tooltip:
      'Comma-separated keywords; the event title must contain every keyword (case-insensitive substring match).',
    appliesTo: 'event',
  },
  {
    key: 'event_title_excludes',
    label: 'Event Title — excludes keyword(s)',
    valueType: 'text',
    placeholder: 'test, draft',
    summaryLabel: 'Title excludes',
    helperText:
      'Workflow runs only if the event title contains none of these keywords (comma-separated, case-insensitive)',
    tooltip:
      'Comma-separated keywords; the workflow is skipped if the event title contains any keyword (case-insensitive).',
    appliesTo: 'event',
  },
  {
    key: 'event_type',
    label: 'Event type',
    valueType: 'select',
    valueOptions: EVENT_TYPE_OPTIONS,
    tooltip:
      'Event category from the events table: webinar, workshop, seminar, conference, or course.',
    appliesTo: 'event',
  },
  {
    key: 'event_location_type',
    label: 'Event location type',
    valueType: 'select',
    valueOptions: EVENT_LOCATION_TYPE_OPTIONS,
    tooltip:
      'Event format: "online" (Zoom), "in_person", or "hybrid". Maps to the events.format column.',
    appliesTo: 'event',
  },
  {
    key: 'event_price',
    label: 'Event price',
    valueType: 'text',
    placeholder: '"free" or a number like 25',
    tooltip:
      'Price string from the event\'s pricing tier (or events.price). Use "free" to match $0/no price, or a numeric string like "25" or "24.99".',
    appliesTo: 'event',
  },
  {
    key: 'agent_id',
    label: 'Agent ID',
    valueType: 'text',
    placeholder: 'UUID of the agent',
    tooltip:
      'UUID of the agent that owns the event (or the lead\'s attributed agent). Useful to scope a workflow to a single agent.',
    appliesTo: 'event',
  },
  {
    key: 'reminder_interval',
    label: 'Reminder interval',
    valueType: 'select',
    valueOptions: REMINDER_INTERVAL_OPTIONS,
    tooltip:
      'Which reminder window the email targets. Only meaningful for event_reminder and event_reminder_unpaid triggers.',
    appliesTo: 'reminder',
  },
];

const FORM_SUBMITTED_CONDITIONS = [
  {
    key: 'form_id',
    label: 'Form ID',
    valueType: 'text',
    placeholder: 'UUID of the marketing form',
    tooltip:
      'UUID of a specific marketing form. Use this to target one form/survey/quiz submission flow.',
    appliesTo: 'form',
  },
  {
    key: 'form_type',
    label: 'Form type',
    valueType: 'select',
    valueOptions: FORM_TYPE_OPTIONS,
    tooltip: 'Type of marketing form: form, survey, or quiz.',
    appliesTo: 'form',
  },
  {
    key: 'event_language',
    label: 'Event / form language',
    valueType: 'select',
    valueOptions: EVENT_LANGUAGE_OPTIONS,
    tooltip:
      'Language on the submission context: form language, or linked event language when the form is tied to an event.',
    appliesTo: 'form',
  },
  {
    key: 'passed',
    label: 'Quiz passed',
    valueType: 'select',
    valueOptions: PASSED_OPTIONS,
    tooltip:
      'For quizzes only: "true" if the respondent met the pass score, "false" otherwise. Empty for non-quiz forms.',
    appliesTo: 'form',
  },
];

const conditionsForTrigger = (trigger) => {
  if (trigger === 'form_submitted') return FORM_SUBMITTED_CONDITIONS;
  return ALL_PRESET_CONDITIONS.filter((c) => {
    if (c.appliesTo === 'reminder') return REMINDER_TRIGGERS.includes(trigger);
    if (c.appliesTo === 'form') return false;
    return true;
  });
};

const TITLE_KEYWORD_PREVIEW_KEYS = new Set(['event_title_includes', 'event_title_excludes']);

const parseTitleKeywords = (raw) =>
  String(raw ?? '')
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

/** Client-side title filters — mirrors execute_workflows keyword condition logic. */
const applyTitleKeywordPreviewFilters = (rows, conditions) => {
  if (!Array.isArray(rows) || !rows.length) return rows ?? [];

  const includesRaw = conditions?.event_title_includes;
  const excludesRaw = conditions?.event_title_excludes;
  const includes = parseTitleKeywords(includesRaw);
  const excludes = parseTitleKeywords(excludesRaw);

  if (!includes.length && !excludes.length) return rows;

  return rows.filter((ev) => {
    const title = (ev.title ?? '').toLowerCase();
    if (includes.length > 0 && !includes.every((kw) => title.includes(kw.toLowerCase()))) {
      return false;
    }
    if (excludes.length > 0 && excludes.some((kw) => title.includes(kw.toLowerCase()))) {
      return false;
    }
    return true;
  });
};

/** Keys the preview API cannot filter server-side (per-trigger context only). */
const filterPreviewIgnoredKeys = (ignoredKeys) =>
  (ignoredKeys ?? []).filter((k) => !TITLE_KEYWORD_PREVIEW_KEYS.has(k));

/** Subtle info icon with a hover/focus tooltip. */
const InfoTooltip = ({ text }) => (
  <span className="relative inline-flex group align-middle ml-1">
    <SafeIcon
      icon={FiInfo}
      className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 cursor-help"
      tabIndex={0}
      title={text}
      aria-label={text}
    />
    <span
      role="tooltip"
      className="pointer-events-none absolute z-20 left-1/2 -translate-x-1/2 top-full mt-1 w-64 hidden group-hover:block group-focus-within:block p-2 text-xs leading-snug bg-slate-800 text-white rounded-md shadow-lg whitespace-normal"
    >
      {text}
    </span>
  </span>
);

const formatDateTime = (iso, timezone) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone || undefined,
    }).format(d);
  } catch {
    return '—';
  }
};

const labelForOption = (options, value) =>
  options.find((o) => o.value === value)?.label ?? (value ?? '—');

const MarketingEmailWorkflows = () => {
  const navigate = useNavigate();
  const { workflowId } = useParams();
  const { profile } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    trigger_event: 'registration_completed',
    template_id: '',
    conditions: {},
    delay_minutes: 0,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [conditionsKey, setConditionsKey] = useState('');
  const [conditionsValue, setConditionsValue] = useState('');
  const [predefinedConditionKey, setPredefinedConditionKey] = useState('');
  const [predefinedConditionValue, setPredefinedConditionValue] = useState('');

  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewIgnoredKeys, setPreviewIgnoredKeys] = useState([]);
  const [previewError, setPreviewError] = useState(null);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    const { data, error } = await marketingService.listWorkflows({ role: 'admin', userId: profile?.id });
    setLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load workflows');
      setWorkflows([]);
      return;
    }
    setWorkflows(Array.isArray(data) ? data : []);
  }, [profile?.id]);

  const loadTemplates = useCallback(async () => {
    const { data, error } = await marketingService.listTemplates({ role: 'admin', userId: profile?.id });
    if (!error && data) setTemplates(Array.isArray(data) ? data : []);
  }, [profile?.id]);

  useEffect(() => {
    loadWorkflows();
    loadTemplates();
  }, [loadWorkflows, loadTemplates]);

  useEffect(() => {
    if (!workflowId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await marketingService.getWorkflowById(workflowId, { role: 'admin', userId: profile?.id });
      if (cancelled || error || !data) return;
      const w = data;
      setEditingId(w.id);
      setForm({
        name: w.name ?? '',
        description: w.description ?? '',
        trigger_event: w.trigger_event ?? 'registration_completed',
        template_id: w.template_id ?? '',
        conditions: w.conditions && typeof w.conditions === 'object' ? w.conditions : {},
        delay_minutes: w.delay_minutes ?? 0,
        is_active: w.is_active ?? true,
      });
      setShowForm(true);
    })();
    return () => { cancelled = true; };
  }, [workflowId, profile?.id]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      description: '',
      trigger_event: 'registration_completed',
      template_id: '',
      conditions: {},
      delay_minutes: 0,
      is_active: true,
    });
    setConditionsKey('');
    setConditionsValue('');
    setPredefinedConditionKey('');
    setPredefinedConditionValue('');
    setShowForm(true);
  };

  const openEdit = (w) => {
    setEditingId(w.id);
    setForm({
      name: w.name ?? '',
      description: w.description ?? '',
      trigger_event: w.trigger_event ?? 'registration_completed',
      template_id: w.template_id ?? '',
      conditions: w.conditions && typeof w.conditions === 'object' ? w.conditions : {},
      delay_minutes: w.delay_minutes ?? 0,
      is_active: w.is_active ?? true,
    });
    setConditionsKey('');
    setConditionsValue('');
    setPredefinedConditionKey('');
    setPredefinedConditionValue('');
    setShowForm(true);
  };

  const predefinedOptions = useMemo(
    () => conditionsForTrigger(form.trigger_event),
    [form.trigger_event]
  );
  const existingConditionKeys = new Set(Object.keys(form.conditions || {}));
  const availablePredefined = predefinedOptions.filter((opt) => !existingConditionKeys.has(opt.key));
  const selectedPredefinedOpt = predefinedOptions.find((opt) => opt.key === predefinedConditionKey);

  const addPredefinedCondition = () => {
    if (!predefinedConditionKey || !String(predefinedConditionValue).trim()) return;
    setForm((f) => ({
      ...f,
      conditions: {
        ...(f.conditions || {}),
        [predefinedConditionKey]: String(predefinedConditionValue).trim(),
      },
    }));
    setPredefinedConditionKey('');
    setPredefinedConditionValue('');
  };

  const addCondition = () => {
    if (!conditionsKey.trim()) return;
    setForm((f) => ({
      ...f,
      conditions: { ...(f.conditions || {}), [conditionsKey.trim()]: conditionsValue.trim() || true },
    }));
    setConditionsKey('');
    setConditionsValue('');
  };

  const removeCondition = (key) => {
    setForm((f) => {
      const next = { ...(f.conditions || {}) };
      delete next[key];
      return { ...f, conditions: next };
    });
  };

  const openPreview = useCallback(async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewError(null);
    const { data, error, ignoredKeys } = await marketingService.previewAffectedEvents(
      form.conditions || {},
      { role: 'admin', userId: profile?.id, limit: 100 }
    );
    setPreviewLoading(false);
    if (error) {
      setPreviewError(error.message ?? 'Failed to preview events');
      setPreviewRows([]);
      setPreviewIgnoredKeys(filterPreviewIgnoredKeys(ignoredKeys));
      return;
    }
    const baseRows = Array.isArray(data) ? data : [];
    setPreviewRows(applyTitleKeywordPreviewFilters(baseRows, form.conditions || {}));
    setPreviewIgnoredKeys(filterPreviewIgnoredKeys(ignoredKeys));
  }, [form.conditions, profile?.id]);

  const closePreview = () => {
    setShowPreview(false);
    setPreviewError(null);
  };

  const saveWorkflow = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      trigger_event: form.trigger_event,
      template_id: form.template_id || null,
      conditions: Object.keys(form.conditions || {}).length ? form.conditions : null,
      delay_minutes: Number(form.delay_minutes) || 0,
      is_active: form.is_active,
    };
    const { data, error } = editingId
      ? await marketingService.updateWorkflow(editingId, payload, { role: 'admin', userId: profile?.id })
      : await marketingService.createWorkflow(payload, { role: 'admin', userId: profile?.id });
    setSaving(false);
    if (error) {
      toast.error(error.message ?? 'Failed to save workflow');
      return;
    }
    toast.success(editingId ? 'Workflow updated' : 'Workflow created');
    setShowForm(false);
    loadWorkflows();
  };

  const duplicateWorkflowRow = async (w) => {
    const { data, error } = await marketingService.duplicateWorkflow(w.id, { role: 'admin', userId: profile?.id });
    if (error) {
      toast.error(error.message ?? 'Failed to duplicate workflow');
      return;
    }
    toast.success('Workflow duplicated');
    await loadWorkflows();
    if (data?.id) {
      navigate(`/marketing/email/workflows/${data.id}`);
      setEditingId(data.id);
      setForm({
        name: data.name ?? '',
        description: data.description ?? '',
        trigger_event: data.trigger_event ?? 'registration_completed',
        template_id: data.template_id ?? '',
        conditions: data.conditions && typeof data.conditions === 'object' ? data.conditions : {},
        delay_minutes: data.delay_minutes ?? 0,
        is_active: data.is_active ?? false,
      });
      setConditionsKey('');
      setConditionsValue('');
      setShowForm(true);
    }
  };

  const deleteWorkflow = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    const { error } = await marketingService.deleteWorkflow(id, { role: 'admin', userId: profile?.id });
    if (error) {
      toast.error(error.message ?? 'Failed to delete');
      return;
    }
    toast.success('Workflow deleted');
    loadWorkflows();
    if (editingId === id) setShowForm(false);
  };

  const activeByTrigger = workflows.reduce((acc, w) => {
    if (w.is_active) acc[w.trigger_event] = (acc[w.trigger_event] || 0) + 1;
    return acc;
  }, {});

  const renderValueEditor = (opt) => {
    if (!opt) return null;
    if (opt.valueType === 'select') {
      return (
        <select
          value={predefinedConditionValue}
          onChange={(e) => setPredefinedConditionValue(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm min-w-[140px]"
        >
          <option value="">Value...</option>
          {opt.valueOptions.map((v) => (
            <option key={v.value} value={v.value}>{v.label}</option>
          ))}
        </select>
      );
    }
    return (
      <div className="flex flex-col gap-1 min-w-[180px]">
        <input
          type="text"
          value={predefinedConditionValue}
          onChange={(e) => setPredefinedConditionValue(e.target.value)}
          placeholder={opt.placeholder ?? 'Value'}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm w-full"
        />
        {opt.helperText ? (
          <p className="text-xs text-slate-500 max-w-xs leading-snug">{opt.helperText}</p>
        ) : null}
      </div>
    );
  };

  const renderConditionChipLabel = (k) => {
    const opt = [...ALL_PRESET_CONDITIONS, ...FORM_SUBMITTED_CONDITIONS].find((o) => o.key === k);
    return opt?.summaryLabel ?? opt?.label ?? k;
  };

  const renderConditionChipValue = (k, v) => {
    const opt = [...ALL_PRESET_CONDITIONS, ...FORM_SUBMITTED_CONDITIONS].find((o) => o.key === k);
    if (!opt) return String(v);
    if (opt.valueType === 'select') {
      const found = opt.valueOptions.find((x) => x.value === v);
      return found ? found.label : String(v);
    }
    return String(v);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800">Email Workflows</h3>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          New workflow
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-gray-100">
              <tr className="text-left text-slate-600">
                <th className="p-4 font-medium">Name</th>
                <th className="p-4 font-medium">Trigger</th>
                <th className="p-4 font-medium">Template</th>
                <th className="p-4 font-medium">Delay</th>
                <th className="p-4 font-medium">Active</th>
                <th className="p-4 font-medium">Active count (trigger)</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {workflows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    No workflows yet. Create one to automate emails.
                  </td>
                </tr>
              ) : (
                workflows.map((w) => (
                  <tr key={w.id} className="border-b border-gray-50 hover:bg-slate-50/50">
                    <td className="p-4 font-medium text-slate-800">{w.name}</td>
                    <td className="p-4 text-slate-600">{TRIGGER_EVENTS.find((e) => e.value === w.trigger_event)?.label ?? w.trigger_event}</td>
                    <td className="p-4 text-slate-600">{w.email_templates?.name ?? (w.template_id ? '—' : 'None')}</td>
                    <td className="p-4 text-slate-600">{w.delay_minutes ?? 0} min</td>
                    <td className="p-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${w.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                        {w.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-slate-500">{activeByTrigger[w.trigger_event] ?? 0}</td>
                    <td className="p-4 flex gap-2">
                      <button onClick={() => openEdit(w)} className="p-2 text-slate-400 hover:text-emerald-600 rounded-lg" title="Edit"><SafeIcon icon={FiEdit} className="w-4 h-4" /></button>
                      <button onClick={() => duplicateWorkflowRow(w)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg" title="Duplicate"><SafeIcon icon={FiCopy} className="w-4 h-4" /></button>
                      <button onClick={() => deleteWorkflow(w.id)} className="p-2 text-slate-400 hover:text-red-600 rounded-lg" title="Delete"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowForm(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit workflow' : 'New workflow'}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Welcome after registration"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  rows={2}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Trigger event</label>
                <select
                  value={form.trigger_event}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, trigger_event: e.target.value }));
                    setPredefinedConditionKey('');
                    setPredefinedConditionValue('');
                  }}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                >
                  {TRIGGER_EVENTS.map((e) => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
                </select>
                {TRIGGER_EVENTS.find((e) => e.value === form.trigger_event)?.description ? (
                  <p className="text-xs text-slate-500 mt-1">
                    {TRIGGER_EVENTS.find((e) => e.value === form.trigger_event).description}
                  </p>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Template</label>
                <select
                  value={form.template_id}
                  onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">None</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Delay (minutes)</label>
                <input
                  type="number"
                  min={0}
                  value={form.delay_minutes}
                  onChange={(e) => setForm((f) => ({ ...f, delay_minutes: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Conditions</label>
                {availablePredefined.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <select
                      value={predefinedConditionKey}
                      onChange={(e) => { setPredefinedConditionKey(e.target.value); setPredefinedConditionValue(''); }}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm min-w-[180px]"
                    >
                      <option value="">Add condition...</option>
                      {availablePredefined.map((opt) => (
                        <option key={opt.key} value={opt.key}>{opt.label}</option>
                      ))}
                    </select>
                    {selectedPredefinedOpt && (
                      <>
                        <span className="inline-flex items-center text-xs text-slate-500">
                          <span className="font-mono px-1.5 py-0.5 bg-slate-100 rounded text-slate-700">{selectedPredefinedOpt.key}</span>
                          <InfoTooltip text={selectedPredefinedOpt.tooltip} />
                        </span>
                        {renderValueEditor(selectedPredefinedOpt)}
                        <button
                          type="button"
                          onClick={addPredefinedCondition}
                          disabled={!String(predefinedConditionValue).trim()}
                          className="px-3 py-2 bg-emerald-100 text-emerald-800 rounded-lg hover:bg-emerald-200 text-sm disabled:opacity-50"
                        >
                          Add
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.conditions && Object.entries(form.conditions).map(([k, v]) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-full text-sm text-slate-700"
                      title={ALL_PRESET_CONDITIONS.find((o) => o.key === k)?.tooltip ?? `Custom condition: ${k}`}
                    >
                      <span className="font-medium">{renderConditionChipLabel(k)}:</span> {renderConditionChipValue(k, v)}
                      <button
                        type="button"
                        onClick={() => removeCondition(k)}
                        className="p-0.5 text-slate-500 hover:text-red-600 rounded"
                        aria-label={`Remove ${k}`}
                      >
                        <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {Object.keys(form.conditions || {}).length === 0 && (
                    <span className="text-xs text-slate-400 italic">
                      No conditions — workflow will fire for every {TRIGGER_EVENTS.find((t) => t.value === form.trigger_event)?.label ?? form.trigger_event} event.
                    </span>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-slate-500 mb-1.5">
                    Add custom condition (key / value)
                    <InfoTooltip text="Use a custom key for any context field not in the preset list. Values are matched as exact strings against the trigger context." />
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={conditionsKey}
                      onChange={(e) => setConditionsKey(e.target.value)}
                      placeholder="Key"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      value={conditionsValue}
                      onChange={(e) => setConditionsValue(e.target.value)}
                      placeholder="Value"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <button type="button" onClick={addCondition} className="px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm">Add custom</button>
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={openPreview}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm font-medium"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4" />
                    Preview affected events &rarr;
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-slate-700">Active</span>
              </label>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button onClick={saveWorkflow} disabled={saving} className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50">
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          onClick={closePreview}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Preview affected events</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Read-only preview of public events that match the conditions currently in the form. Upcoming events
                  are listed first; past events appear below for reference.
                </p>
              </div>
              <button onClick={closePreview} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 bg-slate-50">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-medium text-slate-700">
                  {previewLoading ? (
                    <span className="text-slate-500">Loading…</span>
                  ) : previewError ? (
                    <span className="text-red-600">{previewError}</span>
                  ) : (
                    <>
                      {previewRows.length} event{previewRows.length === 1 ? '' : 's'} match{previewRows.length === 1 ? 'es' : ''} these conditions
                      {(() => {
                        const upcomingCount = previewRows.filter((r) => r.has_upcoming).length;
                        if (previewRows.length === 0) return null;
                        return (
                          <span className="ml-2 text-xs font-normal text-slate-500">
                            ({upcomingCount} upcoming, {previewRows.length - upcomingCount} past)
                          </span>
                        );
                      })()}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <SafeIcon icon={FiList} className="w-3.5 h-3.5" />
                  {Object.keys(form.conditions || {}).length === 0
                    ? 'No conditions set — showing all public events'
                    : `${Object.keys(form.conditions || {}).length} condition(s) applied`}
                </div>
              </div>
              {previewIgnoredKeys.length > 0 && (
                <p className="mt-2 text-xs text-amber-700">
                  Ignored for preview (evaluated per-trigger, not per-event):{' '}
                  <span className="font-mono">{previewIgnoredKeys.join(', ')}</span>
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {previewLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : previewRows.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <SafeIcon icon={FiCalendar} className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="font-medium">No events match these conditions</p>
                  <p className="text-xs mt-1 text-slate-400">
                    Try removing a condition, or check that the event metadata (language, type, format) matches.
                  </p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white sticky top-0 border-b border-gray-100">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-2.5 font-medium">Event</th>
                      <th className="px-4 py-2.5 font-medium">Date</th>
                      <th className="px-4 py-2.5 font-medium">
                        <span className="inline-flex items-center gap-1">
                          <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5" /> Lang
                        </span>
                      </th>
                      <th className="px-4 py-2.5 font-medium">
                        <span className="inline-flex items-center gap-1">
                          <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5" /> Format
                        </span>
                      </th>
                      <th className="px-4 py-2.5 font-medium">Agent</th>
                      <th className="px-4 py-2.5 font-medium text-right">
                        <span className="inline-flex items-center gap-1 justify-end">
                          <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" /> Registered
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((ev) => {
                      const dateIso = ev.display_session_start ?? ev.next_session_start ?? ev.last_session_start ?? null;
                      return (
                        <tr key={ev.id} className="border-b border-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{ev.title || '(Untitled)'}</span>
                              <span
                                className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                  ev.has_upcoming
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : ev.session_count > 0
                                      ? 'bg-slate-200 text-slate-600'
                                      : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {ev.has_upcoming ? 'Upcoming' : ev.session_count > 0 ? 'Past' : 'No sessions'}
                              </span>
                            </div>
                            <div className="text-xs text-slate-400 font-mono truncate max-w-[260px]" title={ev.id}>
                              {ev.id}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                            {formatDateTime(dateIso, ev.timezone)}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {labelForOption(EVENT_LANGUAGE_OPTIONS, ev.language)}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {labelForOption(EVENT_LOCATION_TYPE_OPTIONS, ev.format)}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {ev.agent_name || <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700 font-medium">
                            {ev.registered_count}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={closePreview}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default MarketingEmailWorkflows;
